import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { BookOpen, Bookmark, CreditCard, LogOut, PenLine, Settings, Sparkles, UserCircle, Users, Wind } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const linkClass = ({ isActive }) =>
  `text-sm font-medium px-3 py-2 rounded-full transition-colors ${isActive ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`;

export const Navbar = () => {
  const { user, isAdmin, isTherapist, isOwner, hasAccess, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <header className="sticky top-0 z-40 glass" data-testid="app-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-brand-link">
          <span className="w-9 h-9 rounded-xl bg-indigo-600 text-white grid place-items-center shadow-sm group-hover:bg-indigo-700 transition-colors">
            <Wind className="w-5 h-5" />
          </span>
          <span className="font-display font-extrabold text-xl tracking-tight text-slate-900">Tutorials</span>
        </Link>

        <nav className="hidden sm:flex items-center gap-1">
          {user && hasAccess && <NavLink to="/" end className={linkClass} data-testid="nav-tutorials-link">Library</NavLink>}
          {user && hasAccess && <NavLink to="/my-learning" className={linkClass} data-testid="nav-my-learning-link">My Learning</NavLink>}
          {user && hasAccess && !isTherapist && !isAdmin && <NavLink to="/reflections" className={linkClass} data-testid="nav-reflections-link">Reflections</NavLink>}
          {user && hasAccess && !isTherapist && !isAdmin && <NavLink to="/companion" className={linkClass} data-testid="nav-companion-link">Companion</NavLink>}
          {isTherapist && <NavLink to="/clients" className={linkClass} data-testid="nav-clients-link">Clients</NavLink>}
          {isOwner && <NavLink to="/billing" className={linkClass} data-testid="nav-billing-link">Billing</NavLink>}
          {isAdmin && <NavLink to="/admin" className={linkClass} data-testid="nav-admin-link">Admin</NavLink>}
          {!user && <a href="/#pricing" className="text-sm font-medium px-3 py-2 rounded-full text-slate-600 hover:bg-slate-100" data-testid="nav-pricing-link">Pricing</a>}
        </nav>

        <div className="flex items-center gap-2">
          {user === null ? null : user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 hover:bg-slate-100 transition-colors" data-testid="user-menu-button">
                  <span className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center text-sm font-bold">
                    {(user.name || user.email)[0].toUpperCase()}
                  </span>
                  <span className="hidden sm:block text-sm font-medium text-slate-700 max-w-[120px] truncate">{user.name || user.email}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="truncate">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {hasAccess && <DropdownMenuItem onClick={() => navigate("/")} data-testid="menu-library"><BookOpen className="w-4 h-4 mr-2" />Library</DropdownMenuItem>}
                {hasAccess && <DropdownMenuItem onClick={() => navigate("/my-learning")} data-testid="menu-my-learning"><Bookmark className="w-4 h-4 mr-2" />My Learning</DropdownMenuItem>}
                {hasAccess && <DropdownMenuItem onClick={() => navigate("/reflections")} data-testid="menu-reflections"><PenLine className="w-4 h-4 mr-2" />Reflections</DropdownMenuItem>}
                {hasAccess && <DropdownMenuItem onClick={() => navigate("/companion")} data-testid="menu-companion"><Sparkles className="w-4 h-4 mr-2" />Calm Companion</DropdownMenuItem>}
                {isTherapist && <DropdownMenuItem onClick={() => navigate("/clients")} data-testid="menu-clients"><Users className="w-4 h-4 mr-2" />Clients</DropdownMenuItem>}
                {isOwner && <DropdownMenuItem onClick={() => navigate("/billing")} data-testid="menu-billing"><CreditCard className="w-4 h-4 mr-2" />Billing</DropdownMenuItem>}
                <DropdownMenuItem onClick={() => navigate("/account")} data-testid="menu-account"><UserCircle className="w-4 h-4 mr-2" />Account settings</DropdownMenuItem>
                {isAdmin && <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin"><Settings className="w-4 h-4 mr-2" />Admin panel</DropdownMenuItem>}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { navigate("/", { replace: true }); await logout(); }} data-testid="logout-button"><LogOut className="w-4 h-4 mr-2" />Log out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              {location.pathname !== "/login" && <Button variant="ghost" className="rounded-full" onClick={() => navigate("/login")} data-testid="nav-login-button">Sign in</Button>}
              {location.pathname !== "/apply" && <Button className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => navigate("/apply")} data-testid="nav-apply-button">Apply for access</Button>}
            </>
          )}
        </div>
      </div>
    </header>
  );
};
